import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Linkedin, Shield, Award, Users, Eye, Target, CheckCircle2, TrendingUp, FileCheck, Clock, Headphones, RefreshCw, Scale } from "lucide-react";
import { isRTL } from "@/i18n";
import { teamMembers } from "@/data/properties";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import Breadcrumb from "@/components/Breadcrumb";

const valueIcons = [Eye, Award, Shield];

export default function AboutPage() {
  const { t, i18n } = useTranslation();
  const rtl = isRTL(i18n.language);
  const values = t("about_page.values", { returnObjects: true }) || [];
  const whyUsItems = t("about_page.why_us_items", { returnObjects: true }) || [];
  const servicesItems = t("about_page.services_items", { returnObjects: true }) || [];
  const trustItems = t("about_page.trust_items", { returnObjects: true }) || [];
  const benefitsItems = t("about_page.benefits_items", { returnObjects: true }) || [];
  const milestones = t("about_page.milestones", { returnObjects: true }) || [];

  const whyUsIcons = [Target, TrendingUp, FileCheck, Users, Scale, Headphones];
  const servicesIcons = [TrendingUp, Target, FileCheck, RefreshCw];
  const trustIcons = [Shield, FileCheck, CheckCircle2, Award];
  const benefitsIcons = [Users, Scale, TrendingUp, FileCheck, Clock, RefreshCw];

  return (
    <div className="min-h-screen bg-white" dir={rtl ? "rtl" : "ltr"}>
      <Helmet>
        <title>{t("about_page.seo_title")}</title>
        <meta name="description" content={t("about_page.seo_desc")} />
        <meta name="keywords" content="KKTC yatırım, Kuzey Kıbrıs gayrimenkul, gayrimenkul danışmanlığı, KKTC gayrimenkul yatırımı, Kıbrıs emlak" />
      </Helmet>
      <Header />
      <PageHeader title={t("about_page.hero_title")} highlight={t("about_page.hero_highlight")} title2={t("about_page.hero_title2")} subtitle={t("about_page.hero_subtitle")}>
        <Breadcrumb items={[{ label: t("breadcrumb.about") }]} />
      </PageHeader>

      {/* Vision - Biz Kimiz */}
      <section className="py-16 md:py-24 px-6 md:px-12" data-testid="vision-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="gold-line" /><span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("about_page.vision_pretitle")}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("about_page.vision_title")} <span className="text-[#D4AF37] italic">{t("about_page.vision_highlight")}</span>
            </h2>
            <p className="text-[#4A5568] text-base leading-relaxed mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("about_page.vision_desc1")}</p>
            <p className="text-[#4A5568] text-base leading-relaxed mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("about_page.vision_desc2")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {values.map((v, i) => {
                const Icon = valueIcons[i];
                return (
                  <div key={i} data-testid={`value-card-${i}`} className="p-5 bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                    <Icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                    <h3 className="text-sm font-semibold text-[#0A192F] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{v.title}</h3>
                    <p className="text-xs text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>{v.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&h=600&fit=crop" alt="OlyInvestment Office" className="w-full h-[400px] lg:h-[500px] object-cover rounded-sm" />
            <div className={`absolute -top-4 ${rtl ? "-right-4" : "-left-4"} w-24 h-24 ${rtl ? "border-t-4 border-r-4 rounded-tr-sm" : "border-t-4 border-l-4 rounded-tl-sm"} border-[#D4AF37]`} />
            <div className={`absolute -bottom-4 ${rtl ? "-left-4" : "-right-4"} w-24 h-24 ${rtl ? "border-b-4 border-l-4 rounded-bl-sm" : "border-b-4 border-r-4 rounded-br-sm"} border-[#D4AF37]`} />
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#F8F9FA]" data-testid="mission-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="gold-line" /><span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("about_page.mission_pretitle")}</span><div className="gold-line" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("about_page.mission_title")} <span className="text-[#D4AF37] italic">{t("about_page.mission_highlight")}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-sm border border-[#E2E8F0]">
              <div className="flex items-center gap-3 mb-4">
                <Target className="w-8 h-8 text-[#D4AF37]" />
                <h3 className="text-xl font-bold text-[#0A192F]" style={{ fontFamily: "'Playfair Display', serif" }}>Misyon</h3>
              </div>
              <p className="text-[#4A5568] text-base leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("about_page.mission_text")}</p>
            </div>
            <div className="bg-white p-8 rounded-sm border border-[#E2E8F0]">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-8 h-8 text-[#D4AF37]" />
                <h3 className="text-xl font-bold text-[#0A192F]" style={{ fontFamily: "'Playfair Display', serif" }}>Vizyon</h3>
              </div>
              <p className="text-[#4A5568] text-base leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("about_page.vision_long_text")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 px-6 md:px-12" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="gold-line" /><span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("about_page.why_us_pretitle")}</span><div className="gold-line" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("about_page.why_us_title")} <span className="text-[#D4AF37] italic">{t("about_page.why_us_highlight")}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyUsItems.map((item, i) => {
              const Icon = whyUsIcons[i] || CheckCircle2;
              return (
                <div key={i} data-testid={`why-us-${i}`} className="bg-[#F8F9FA] p-6 rounded-sm border border-[#E2E8F0] hover:shadow-lg transition-shadow duration-300">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0A192F] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                  <p className="text-sm text-[#4A5568] leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#F8F9FA]" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="gold-line" /><span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("about_page.services_pretitle")}</span><div className="gold-line" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("about_page.services_title")} <span className="text-[#D4AF37] italic">{t("about_page.services_highlight")}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {servicesItems.map((item, i) => {
              const Icon = servicesIcons[i] || TrendingUp;
              return (
                <div key={i} data-testid={`service-${i}`} className="bg-white p-8 rounded-sm border border-[#E2E8F0] hover:shadow-xl transition-all duration-300">
                  <Icon className="w-10 h-10 text-[#D4AF37] mb-4" />
                  <h3 className="text-xl font-bold text-[#0A192F] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                  <p className="text-[#4A5568] text-base leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust & Transparency */}
      <section className="py-16 md:py-24 px-6 md:px-12" data-testid="trust-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="gold-line" /><span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("about_page.trust_pretitle")}</span><div className="gold-line" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("about_page.trust_title")} <span className="text-[#D4AF37] italic">{t("about_page.trust_highlight")}</span>
            </h2>
            <p className="text-[#4A5568] text-lg leading-relaxed max-w-3xl mx-auto" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("about_page.trust_desc")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trustItems.map((item, i) => {
              const Icon = trustIcons[i] || Shield;
              return (
                <div key={i} data-testid={`trust-${i}`} className="text-center p-6 bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                  <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-base font-semibold text-[#0A192F] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                  <p className="text-sm text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Investor Benefits */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#F8F9FA]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="gold-line" /><span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("about_page.benefits_pretitle")}</span><div className="gold-line" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("about_page.benefits_title")} <span className="text-[#D4AF37] italic">{t("about_page.benefits_highlight")}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefitsItems.map((item, i) => {
              const Icon = benefitsIcons[i] || CheckCircle2;
              return (
                <div key={i} data-testid={`benefit-${i}`} className="bg-white p-6 rounded-sm border border-[#E2E8F0] hover:shadow-lg transition-shadow duration-300">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0A192F] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                  <p className="text-sm text-[#4A5568] leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 md:py-24 px-6 md:px-12" data-testid="team-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="gold-line" /><span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("about_page.team_pretitle")}</span><div className="gold-line" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("about_page.team_title")} <span className="text-[#D4AF37] italic">{t("about_page.team_highlight")}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((m) => (
              <div key={m.id} data-testid={`team-member-${m.id}`} className="group bg-white rounded-sm border border-[#E2E8F0] overflow-hidden hover:shadow-xl transition-all duration-500">
                <div className="relative h-64 overflow-hidden">
                  <img src={m.image} alt={m.name} className="w-full h-full object-cover card-img-zoom" />
                  <a href={m.linkedin} target="_blank" rel="noopener noreferrer" className="absolute bottom-4 right-4 w-10 h-10 bg-[#0A192F]/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 hover:bg-[#D4AF37]">
                    <Linkedin className="w-4 h-4 text-white" />
                  </a>
                </div>
                <div className="p-5 text-center">
                  <h3 className="text-base font-semibold text-[#0A192F]" style={{ fontFamily: "'Playfair Display', serif" }}>{m.name}</h3>
                  <p className="text-sm text-[#D4AF37]" style={{ fontFamily: "'Outfit', sans-serif" }}>{m.role[i18n.language] || m.role.en}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#F8F9FA]" data-testid="milestones-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="gold-line" /><span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("about_page.milestones_pretitle")}</span><div className="gold-line" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>{t("about_page.milestones_title")}</h2>
          </div>
          <div className="relative">
            <div className={`absolute ${rtl ? "right-6" : "left-6"} top-0 bottom-0 w-px bg-[#E2E8F0]`} />
            <div className="space-y-10">
              {milestones.map((m, i) => (
                <div key={i} data-testid={`milestone-${i}`} className={`flex items-start gap-6 ${rtl ? "pr-0" : "pl-0"}`}>
                  <div className="relative z-10 shrink-0 w-12 h-12 rounded-full bg-[#D4AF37] text-white flex items-center justify-center text-xs font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>{m.year}</div>
                  <div className="pt-2">
                    <h3 className="text-base font-semibold text-[#0A192F] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{m.title}</h3>
                    <p className="text-sm text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
